
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { URLS } from './apiurls.constants';

interface TranslatePayload {
  texts: string[];
  sourceLang: string;
  targetLang: string;
}

interface CachedTranslationData {
  translations: { [key: string]: string };
  originals: { [key: string]: string };
  timestamp: number;
}

@Injectable({ providedIn: 'root' })
export class TranslateService {
  private apiUrl = `${environment.url_point + URLS.Translate.AwsTranslation}`;
  private observer: MutationObserver | null = null;
  private lastSourceLang = 'en';
  private lastTargetLang = 'en';
  private currentLang = 'en';
  private isTranslating = false;

  private translationCache = new Map<string, string>();
  private originalTextCache = new Map<string, string>();
  private readonly CACHE_EXPIRATION_MS = 24 * 60 * 60 * 1000; // 24 hours
  private maxTranslationsPerMinute = 200;
  private translationQueueCount = 0;
  private lastReset = Date.now();

  constructor(private http: HttpClient) {
    this.loadCacheFromSession();
  }

  getCurrentLang(): string {
    return this.currentLang;
  }

  setTargetLang(targetLang: string): void {
    this.currentLang = targetLang;
  }

  // triggerTranslation(): void {
  //   this.translateWholePage(this.lastSourceLang, this.currentLang);
  // }

  public triggerTranslation(): void {
  this.waitForDomToRender(() => {
    this.translateWholePage(this.lastSourceLang, this.currentLang);
  });
}

private waitForDomToRender(callback: () => void): void {
  const maxWaitMs = 2000;
  const intervalMs = 100;
  let waited = 0;

  const check = () => {
    const mainContent = document.querySelector('router-outlet') || document.body;
    const hasContent = mainContent?.textContent?.trim().length ?? 0;

    if (hasContent > 0 || waited >= maxWaitMs) {
      callback();
    } else {
      waited += intervalMs;
      setTimeout(() => requestAnimationFrame(check), intervalMs);
    }
  };

  requestAnimationFrame(check);
}


  async translateWholePage(sourceLang: string, targetLang: string): Promise<void> {
    this.currentLang = targetLang;
    if (sourceLang === targetLang || this.isTranslating) return;

    const now = Date.now();
    if (now - this.lastReset > 60000) {
      this.translationQueueCount = 0;
      this.lastReset = now;
    }

    if (this.translationQueueCount > this.maxTranslationsPerMinute) {
      console.warn('Translation limit reached');
      return;
    }

    this.isTranslating = true;
    this.disconnectObserver();

    try {
      this.lastSourceLang = sourceLang;
      this.lastTargetLang = targetLang;

      const container = document.body;
      const textNodes = this.getVisibleTextNodes(container);
      const nodesToTranslate: Text[] = [];
      const originalTexts: string[] = [];

      for (const node of textNodes) {
        const trimmedText = node.textContent?.trim() ?? '';
        const parent = node.parentElement;
        if (!trimmedText || !parent) continue;

        const translatedFlag = parent.getAttribute('data-translated');
        const originalAttr = parent.getAttribute('data-original');

        if (targetLang === 'en') {
        // console.log("hi modal");
          if (originalAttr) {
            node.textContent = originalAttr;
            parent.removeAttribute('data-translated');
            continue;
          }
        }

        if (translatedFlag) continue;

        const cached = this.translationCache.get(trimmedText);
        if (cached) {
          parent.setAttribute('data-original', trimmedText);
          node.textContent = cached;
          parent.setAttribute('data-translated', 'true');
        } else {
          nodesToTranslate.push(node);
          originalTexts.push(trimmedText);
        }
      }

      const excludedFormsString = sessionStorage.getItem('Form');
      const sessionExcluded = excludedFormsString ? excludedFormsString.split(',').map(f => f.trim()) : [];
      const hardcodedExcluded = [
        'Health Record', 'My Team', 'Educational', 'Dashboards', 'Messages',
        'Documentation Center', 'Bill Pay', 'Surveys', 'Profile','Assigned Staff','Non Medication Allergies','Encounters','Plan Of Nursing Services',
        'Clinic Satisfaction Survey', 'Online Bill Pay', 'Educational Resources', 'Dashboard','Secure Messages','Appointment','Packets Details'
      ];
      const allExcludedForms = [...sessionExcluded, ...hardcodedExcluded].map(f => f.toLowerCase());

      const filteredOriginalTexts: string[] = [];
      const filteredNodesToTranslate: Text[] = [];

      for (let i = 0; i < originalTexts.length; i++) {
        const text = originalTexts[i].trim();
        if (!allExcludedForms.includes(text.toLowerCase())) {
          filteredOriginalTexts.push(text);
          filteredNodesToTranslate.push(nodesToTranslate[i]);
        }
      }

      if (filteredOriginalTexts.length === 0) {
        this.isTranslating = false;
        return;
      }

      const payload: TranslatePayload = {
        texts: filteredOriginalTexts,
        sourceLang,
        targetLang
      };

      const res: any = await this.http.post(this.apiUrl, payload).toPromise();
      let i = 0;
      for (const node of filteredNodesToTranslate) {
        const translated = res.translated[i++];
        const originalText = node.textContent?.trim();
        const parent = node.parentElement;

        if (originalText && translated && parent) {
          parent.setAttribute('data-original', originalText);
          node.textContent = translated;
          parent.setAttribute('data-translated', 'true');
          this.translationCache.set(originalText, translated);
          this.originalTextCache.set(originalText, originalText);
        }
      }

      this.translationQueueCount += filteredOriginalTexts.length;
      this.saveCacheToSession();
    } catch (err) {
      console.error('Translation failed:', err);
    } finally {
      this.isTranslating = false;
      setTimeout(() => this.observeDomChanges(this.lastSourceLang, this.currentLang), 1000);
    }
  }

  observeDomChanges(sourceLang: string, targetLang: string): void {
    this.disconnectObserver();
    this.lastSourceLang = sourceLang;
    this.lastTargetLang = targetLang;
    this.currentLang = targetLang;

    this.observer = new MutationObserver((mutations) => {
      const shouldTranslate = mutations.some(mutation =>
        Array.from(mutation.addedNodes).some(node =>
          node.nodeType === Node.TEXT_NODE ||
          (node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).innerText.trim().length > 0)
        )
      );

      if (shouldTranslate) {
        this.triggerTranslation();
      }
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: false
    });
  }

  disconnectObserver(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  private getVisibleTextNodes(root: HTMLElement): Text[] {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        const text = node.textContent?.trim();
        const parent = node.parentElement;

        if (!parent || !text) return NodeFilter.FILTER_REJECT;

        const style = getComputedStyle(parent);
        const isVisible = style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
        const isInTooltip = parent.closest('.my-tooltip-class') !== null || parent.id === 'tooltip-container';

        return isVisible && !isInTooltip ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      }
    });

    const nodes: Text[] = [];
    let currentNode: Text | null;
    while ((currentNode = walker.nextNode() as Text | null)) {
      nodes.push(currentNode);
    }

    return nodes;
  }

  private debounce<T extends (...args: any[]) => void>(func: T, wait: number): T {
    let timeout: ReturnType<typeof setTimeout>;
    return ((...args: Parameters<T>) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    }) as T;
  }

  private saveCacheToSession(): void {
    const translationObj: { [key: string]: string } = {};
    const originalObj: { [key: string]: string } = {};

    this.translationCache.forEach((val, key) => {
      translationObj[key] = val;
    });

    this.originalTextCache.forEach((val, key) => {
      originalObj[key] = val;
    });

    const data: CachedTranslationData = {
      translations: translationObj,
      originals: originalObj,
      timestamp: Date.now()
    };

    sessionStorage.setItem('TranslationCacheFull', JSON.stringify(data));
  }

  private loadCacheFromSession(): void {
    const cacheStr = sessionStorage.getItem('TranslationCacheFull');
    if (!cacheStr) return;

    try {
      const parsed: CachedTranslationData = JSON.parse(cacheStr);
      const now = Date.now();

      if ((now - parsed.timestamp) > this.CACHE_EXPIRATION_MS) {
        sessionStorage.removeItem('TranslationCacheFull');
        return;
      }

      for (const key in parsed.translations) {
        this.translationCache.set(key, parsed.translations[key]);
      }

      for (const key in parsed.originals) {
        this.originalTextCache.set(key, parsed.originals[key]);
      }
    } catch {
      sessionStorage.removeItem('TranslationCacheFull');
    }
  }
}
